/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { AppLogo, ConfirmModal } from "@advapacs/common";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const OauthCallbackTKeys = {
  existingAccount: "clinician_portal.existing_account",
  emailAlreadyRegistered: "clinician_portal.email_already_registered",
  confirmMessage: "clinician_portal.would_you_like_signin",
  noAccountFound: "clinician_portal.no_account_found",
  couldNotFoundAccount: "clinician_portal.could_not_find_account",
  createAccountWithEmail: "clinician_portal.create_account_with_email",
};

const OauthCallback: FC = () => {
  const { t } = useTranslation();
  const pageLoaded = useRef(false);
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );
  const [email, setEmail] = useState("");
  const [showExitsAccount, setShowExitsAccount] = useState(false);
  const [, setAction] = useState("");

  useEffect(() => {
    if (queryParams && !pageLoaded?.current) {
      pageLoaded.current = true;
      const failureCode = queryParams.get("failureCode");
      const tokenParams = queryParams.get("token");
      const emailParams = queryParams.get("email");
      const providerParams = queryParams.get("provider");
      const actionParams = queryParams.get("action");
      setEmail(emailParams);
      setAction(actionParams);
      if (failureCode) {
        // handle failed case/
        if (failureCode === "8101") {
          setShowExitsAccount(true);
        }
        return;
      } else {
        switch (actionParams) {
          case "signup":
            const shouldRedirectToSignUp =
              tokenParams !== "" && providerParams !== "" && emailParams !== "";
            if (shouldRedirectToSignUp) {
              window.location.href = `/Signup/Individual?type=${providerParams}&email=${emailParams}&token=${tokenParams}`;
            }
            break;
          case "login":
            const shouldRedirectToLogin =
              tokenParams !== "" && providerParams !== "" && emailParams !== "";
            if (shouldRedirectToLogin) {
              // dispatch getUser to authentication process.
              let brandData: { brandPath: string } | undefined;
              try {
                const brandDataRaw = localStorage.getItem("brand_portal");
                if (brandDataRaw && brandDataRaw !== "undefined") {
                  brandData = JSON.parse(brandDataRaw);
                }
              } catch {
                brandData = undefined;
              }
              if (brandData?.brandPath) {
                window.location.href = `/brand/${brandData.brandPath}?token=${tokenParams}&email=${emailParams}`;
              } else
                window.location.href = `/Login?token=${tokenParams}&email=${emailParams}`;
            }
            break;
          default:
            break;
        }
      }
    }
  }, [queryParams, pageLoaded]);

  return (
    <>
      {showExitsAccount && (
        <ConfirmModal
          header={
            <h4 className="text-center ">
              {t(OauthCallbackTKeys.existingAccount)}
            </h4>
          }
          show={showExitsAccount}
          icon={
            <div>
              <AppLogo.CPLogoMain />
            </div>
          }
          body={
            <>
              <div className="m-b-xxs text-center">
                {t(OauthCallbackTKeys.emailAlreadyRegistered, {
                  email: email,
                })}
              </div>

              <div className="m-b-sm text-center">
                {t(OauthCallbackTKeys.confirmMessage)}
              </div>
            </>
          }
          cancelBtnName={t("common:no")}
          confirmBtnName={t("common:yes")}
          cancelBtnVariant="light"
          confirmBtnVariant="primary"
          confirm={() => {
            setShowExitsAccount(false);
            window.location.href = `/`;
          }}
          cancel={() => {
            setShowExitsAccount(false);
            window.location.href = `/Signup/Individual`;
          }}
        />
      )}
    </>
  );
};

export default OauthCallback;
